import React, { useState, useEffect } from 'react';
import apiClient from '../api';
import '../styles/github.css';
import ParticlesBackground from './ParticlesBackground';

function Register({ onRegisterSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('memo_theme') || 'dark';
    setTheme(savedTheme);
    document.body.classList.add(savedTheme + '-theme');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('memo_theme', newTheme);
    document.body.classList.remove(theme + '-theme');
    document.body.classList.add(newTheme + '-theme');
  };

  // 发送验证码函数
  const sendVerificationCode = async () => {
    if (!email) {
      setRegisterError('请输入邮箱');
      return;
    }
    
    // 简单的邮箱格式验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setRegisterError('邮箱格式不正确');
      return;
    }
    
    setRegisterError('');
    setLoading(true);
    
    try {
      const response = await apiClient.post('/send_verification_code', {
        email,
        purpose: 'register'
      });
      
      setCodeSent(true);
      setCountdown(60);
      
      // 开始倒计时
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCodeSent(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      alert('验证码已发送到邮箱，请查收');
      if (response.data.debug_code) {
        console.log('测试用验证码:', response.data.debug_code);
      }
    } catch (err) {
      console.error('发送验证码失败:', err);
      setRegisterError(err.response?.data?.error || '发送验证码失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRegisterError('');
    
    // 表单验证
    if (password !== confirmPassword) {
      setRegisterError('两次输入的密码不一致');
      return;
    }
    
    if (!email || !verificationCode) {
      setRegisterError('邮箱和验证码都是必填项');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post('/register', {
        username,
        password,
        email,
        verification_code: verificationCode
      });
      
      console.log('注册成功:', response.data);
      alert('注册成功！请登录。');
      if (onRegisterSuccess) onRegisterSuccess();
    } catch (err) {
      console.error('注册失败:', err);
      setRegisterError(err.response?.data?.error || '注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.pageContainer}>
      {/* 动态背景 */}
      <div className={`dynamic-bg ${theme}-theme`} />
      <ParticlesBackground theme={theme} />
      
      {/* 主题切换按钮 */}
      <div className="theme-toggle" onClick={toggleTheme} style={styles.themeToggle}>
        {theme === 'dark' ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#f5f0e6">
            <path d="M9.37 5.51c-.18.64-.27 1.31-.27 1.99 0 4.08 3.32 7.4 7.4 7.4.68 0 1.35-.09 1.99-.27C17.45 17.19 14.93 19 12 19c-3.86 0-7-3.14-7-7 0-2.93 1.81-5.45 4.37-6.49zM12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#f9c796a4">
            <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41.39.39 1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41.39.39 1.03.39 1.41 0l1.06-1.06z"/>
          </svg>
        )}
      </div>

      <main style={styles.main}>
        <div style={styles.container}>
          <div style={styles.contentWrapper}>
            <div style={styles.heroSection}>
              <h1 className="rainbow-text" style={styles.heroTitle}>加入 MemoHub</h1>
              <p className="rainbow-text" style={styles.heroSubtitle}>开始使用MemoHub云端备忘录</p>
            </div>
            
            <div className="register-form-container" style={styles.formContainer}>
              <div className="card" style={styles.formCard}>
                <div style={styles.formHeader}>
                  <h2 style={styles.formTitle}>创建新账号</h2>
                  <p style={styles.formDescription}>
                    注册需要验证邮箱
                  </p>
                </div>
                
                {registerError && (
                  <div style={styles.errorAlert}>
                    <svg style={styles.errorIcon} viewBox="0 0 16 16" fill="currentColor">
                      <path fillRule="evenodd" d="M8.22 1.754a.25.25 0 00-.44 0L1.698 13.132a.25.25 0 00.22.368h12.164a.25.25 0 00.22-.368L8.22 1.754zm-1.763-.707c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0114.082 15H1.918a1.75 1.75 0 01-1.543-2.575L6.457 1.047zM9 11a1 1 0 11-2 0 1 1 0 012 0zm-.25-5.25a.75.75 0 00-1.5 0v2.5a.75.75 0 001.5 0v-2.5z"/>
                    </svg>
                    {registerError}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} style={styles.form}>
                  <div style={styles.formGroup}>
                    <label htmlFor="username" style={styles.label}>
                      用户名
                    </label>
                    <input
                      id="username"
                      type="text"
                      className="input-field input-field-full"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="选择一个用户名"
                      required
                      style={styles.input}
                    />
                    <small style={styles.inputHint}>
                      只能包含字母、数字和连字符
                    </small>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label htmlFor="email" style={styles.label}>
                      邮箱
                    </label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input
                        id="email"
                        type="email"
                        className="input-field input-field-full"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="输入您的邮箱"
                        required
                        disabled={codeSent}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={sendVerificationCode}
                        className="btn btn-secondary"
                        disabled={codeSent || loading || countdown > 0}
                        style={{ minWidth: '120px', whiteSpace: 'nowrap' }}
                      >
                        {countdown > 0 ? `${countdown}秒后重试` : '发送验证码'}
                      </button>
                    </div>
                    <small style={styles.inputHint}>
                      用于验证账户和找回密码
                    </small>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label htmlFor="verificationCode" style={styles.label}>
                      验证码
                    </label>
                    <input
                      id="verificationCode"
                      type="text"
                      className="input-field input-field-full"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="请输入6位验证码"
                      required
                      maxLength="6"
                      style={styles.input}
                    />
                    <small style={styles.inputHint}>
                      验证码已发送到您的邮箱，10分钟内有效
                    </small>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label htmlFor="password" style={styles.label}>
                      密码
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        className="input-field input-field-full"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="创建安全密码"
                        required
                        style={{ ...styles.input, paddingRight: '40px' }}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          color: 'var(--color-text-tertiary)',
                          fontSize: '18px',
                          userSelect: 'none',
                          zIndex: 2,
                        }}
                      >
                        {showPassword ? "🙈" : "👁️"}
                      </span>
                    </div>
                    <small style={styles.inputHint}>
                      至少8个字符，包含字母和数字
                    </small>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label htmlFor="confirmPassword" style={styles.label}>
                      确认密码
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        className="input-field input-field-full"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="再次输入密码"
                        required
                        style={{ ...styles.input, paddingRight: '40px' }}
                      />
                      <span
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          color: 'var(--color-text-tertiary)',
                          fontSize: '18px',
                          userSelect: 'none',
                          zIndex: 2,
                        }}
                      >
                        {showConfirmPassword ? "🙈" : "👁️"}
                      </span>
                    </div>
                  </div>
                  
                 <div style={styles.terms}>
                    <label style={styles.checkboxLabel}>
                        <input type="checkbox" required style={styles.checkbox} />
                        <span style={styles.checkboxText}>
                        我同意{' '}
                        <a 
                            href="/terms" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{color: 'var(--color-accent)', textDecoration: 'none'}}
                            onClick={(e) => {
                            e.preventDefault();
                            window.open('/terms', '_blank');
                            }}
                        >
                            服务条款
                        </a>
                        {' 和 '}
                        <a 
                            href="/privacy" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{color: 'var(--color-accent)', textDecoration: 'none'}}
                            onClick={(e) => {
                            e.preventDefault();
                            window.open('/privacy', '_blank');
                            }}
                        >
                            隐私政策
                        </a>
                        </span>
                    </label>
                  </div>
                  
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={styles.submitButton}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner" style={{marginRight: '8px'}}></span>
                        注册中...
                      </>
                    ) : '创建账号'}
                  </button>
                </form>
                
                <div style={styles.formFooter}>
                  <p style={styles.footerText}>
                    已经有账号了？{' '}
                    <a href="/login" style={styles.footerLink}>
                      立即登录
                    </a>
                  </p>
                </div>
              </div>
              
              <div style={styles.features}>
                <div className="card" style={styles.featureCard}>
                  <h3 style={styles.featureTitle}>为什么选择 MemoHub？</h3>
                  <ul style={styles.featureList}>
                    <li style={styles.featureItem}>📧 邮箱验证，安全可靠</li>
                    <li style={styles.featureItem}>🔒 端到端加密保护</li>
                    <li style={styles.featureItem}>📱 跨设备同步</li>
                    <li style={styles.featureItem}>🎨 极简风格界面</li>
                    <li style={styles.featureItem}>⚡ 快速响应</li>
                    <li style={styles.featureItem}>💾 云端存储</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <footer style={styles.footer}>
        <div className="container">
          <p style={styles.footerText}>
            © 2025 MemoHub. 备忘录应用。
          </p>
        </div>
      </footer>
    </div>
  );
}

// 样式保持不变
const styles = {
  pageContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  themeToggle: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 100,
  },
  main: {
    flex: 1,
    padding: '60px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
  },
  heroSection: {
    textAlign: 'center',
    paddingBottom: '40px',
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: '700',
    marginBottom: '16px',
    lineHeight: '1.2',
    animation: 'rainbow 3s infinite linear',
  },
  heroSubtitle: {
    fontSize: '20px',
    maxWidth: '600px',
    margin: '0 auto',
    animation: 'rainbow 3s infinite linear',
  },
  formContainer: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 0.8fr',
    gap: '40px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  formCard: {
    padding: '48px 40px',
    display: 'flex',
    flexDirection: 'column',
  },
  formHeader: {
    marginBottom: '32px',
    textAlign: 'center',
  },
  formTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--color-text-primary)',
    marginBottom: '12px',
  },
  formDescription: {
    fontSize: '16px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5',
  },
  errorAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 18px',
    backgroundColor: 'rgba(203, 36, 49, 0.1)',
    border: '1px solid rgba(203, 36, 49, 0.3)',
    borderRadius: '8px',
    color: 'var(--color-btn-danger)',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500',
  },
  errorIcon: {
    width: '18px',
    height: '18px',
    flexShrink: 0,
  },
  form: {
    marginBottom: '30px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: '28px',
    width: '100%',
  },
  label: {
    display: 'block',
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '10px',
    color: 'var(--color-text-primary)',
  },
  input: {
    width: '100%',
    padding: '15px 18px',
    fontSize: '15px',
    boxSizing: 'border-box',
  },
  inputHint: {
    display: 'block',
    fontSize: '13px',
    color: 'var(--color-text-tertiary)',
    marginTop: '6px',
  },
  terms: {
    marginBottom: '20px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
  },
  checkbox: {
    marginRight: '10px',
    accentColor: 'var(--color-accent)',
  },
  checkboxText: {
    userSelect: 'none',
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    fontSize: '16px',
    fontWeight: '600',
    marginTop: '32px',
    borderRadius: '8px',
  },
  formFooter: {
    paddingTop: '20px',
    borderTop: '1px solid var(--color-border-primary)',
    textAlign: 'center',
    marginTop: '20px',
  },
  footerText: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
  },
  footerLink: {
    color: 'var(--color-accent)',
    textDecoration: 'none',
    fontWeight: '600',
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
  },
  featureCard: {
    padding: '32px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  featureTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '20px',
    color: 'var(--color-text-primary)',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    flex: 1,
  },
  featureItem: {
    padding: '12px 0',
    color: 'var(--color-text-primary)',
    fontSize: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderBottom: '1px solid var(--color-border-primary)',
  },
  footer: {
    backgroundColor: 'var(--color-header-bg)',
    color: 'var(--color-header-text)',
    borderTop: '1px solid var(--color-border-primary)',
    padding: '30px 0',
    textAlign: 'center',
    marginTop: 'auto',
  },
};

export default Register;